import {
    ModalOverlay,
    ModalDialog,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    LoadingDots,
    Message
} from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import getAdjustCoverageConfig from '../config/AdjustCoverageConfig';

import type { AdjustCoverageInformationProps, HandleCloseOPCModal } from '../types';
import { useAdjustCoverageOPC } from '../../../hooks/useAdjustCoverageOPC';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import useOpcJob from '../../../hooks/useOpcJob';
import usePageNavigation from '../../../hooks/usePageNavigation';
import AttributeChangeInformation from './EditCoverableAttribute/AttributeChangeInformation';
import { callMeVariantOpcAtom } from '../../../recoil/atoms/callMeVariantOpcAtom';
import {
    ACC_DEATH_COVERAGE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_EIGHT_PERCENT,
    ACC_INJURY_TOOTH_DAMAGE_FIVE_PERCENT,
    ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
} from '../../../constants/term';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { track } from '../../../utils/trackingUtil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import { refreshCoverableChangesSelectorAtom } from '../../../recoil/atoms/refreshCoverableChangesSelectorAtom';
import { opcCoverableStatusSelector } from '../../../recoil/selectors/opcCoverableStatusSelector';

const AdjustCoverageInfo = ({
    adjustInformationProps
}: {
    adjustInformationProps: AdjustCoverageInformationProps;
}): React.JSX.Element => {
    return <AttributeChangeInformation attributeChangeProps={adjustInformationProps} />;
};

const AdjustCoverageModal = ({
    handleCloseModal,
    shouldNavigateToShoppingBasket
}: {
    handleCloseModal: HandleCloseOPCModal;
    shouldNavigateToShoppingBasket: boolean;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const isFullScreen = false;
    const {
        termInfo,
        editTermPriceDTO,
        updateTermValue,
        updateTermStringValue,
        adjustAction,
        isAdjustableDataValid,
        validateAdjustableDeductible,
        revertChange
    } = useAdjustCoverageOPC();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const isOpcDataFilled = useRecoilValue(isOpcDataFilledAtom);
    const { selectedPaymentFrequency } = useRecoilValue(jobAtom);
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);
    const { createNewPolicyChange, withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { goToShoppingBasket } = usePageNavigation();
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);
    const setRefreshCoverableChangesSelector = useSetRecoilState(
        refreshCoverableChangesSelectorAtom
    );
    const { isAddedCoverable } = useRecoilValue(opcCoverableStatusSelector);
    const shouldHideDateText = isFromDAFlow && isAddedCoverable;
    const maximumValue = termInfo?.valueMax ?? 0;

    if (!adjustAction) throw new Error('Unknown action');

    if (!termInfo || !editTermPriceDTO) return <LoadingDots />;

    const { title, AdjustComponent, adjustInformationProps } = getAdjustCoverageConfig(
        adjustAction,
        editTermPriceDTO,
        termInfo.directValue ?? 0,
        termInfo.value ?? '0',
        selectedPaymentFrequency
    );

    const hasIncreaseInjuryDegree =
        termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM &&
        termInfo.value === String(ACC_INJURY_TOOTH_DAMAGE_FIVE_PERCENT) &&
        editTermPriceDTO.newTermValue?.amount === ACC_INJURY_TOOTH_DAMAGE_EIGHT_PERCENT;

    const exceededDeathCoverageLimit =
        termInfo.publicId === ACC_DEATH_COVERAGE_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const exceededPermanentInjuryInsuredSum =
        termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const exceededChildPermanentInjuryInsuredSum =
        termInfo.publicId === CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const lowerAccInsuranceSumThanPrevious =
        adjustAction === 'Limit' &&
        (termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM ||
            termInfo.publicId === CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM) &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) < Number(termInfo.value);

    if (!adjustInformationProps) return <LoadingDots />;

    const trackChangedCoverage = (): void => {
        if (!editTermPriceDTO.coverageName || !editTermPriceDTO.coverableName) return;
        const trackingKey = adjustAction === 'Limit' ? 'InsuranceSum' : adjustAction;

        track(
            `OPCEditCoverage-${editTermPriceDTO.coverableName}-${editTermPriceDTO.coverageName}`,
            [
                { newYearlyPrice: editTermPriceDTO.newPrice?.yearlyTotal.amount },
                { newMonthlyPrice: editTermPriceDTO.newPrice?.monthlyTotal.amount },
                { [trackingKey]: editTermPriceDTO.newTermValue?.amount }
            ]
        );
    };

    const handleClearChange = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        await createNewPolicyChange();
    };

    const handleRevertChange = async (): Promise<void> => {
        await revertChange();
        shouldNavigateToShoppingBasket && goToShoppingBasket(false);
    };

    const closeCallback = isFromDAFlow ? handleRevertChange : handleClearChange;

    return (
        <ModalOverlay isOpen={true} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(closeCallback);
                }}
            >
                <ModalHeader
                    title={`${getHippoText(title)}: ${editTermPriceDTO.coverageName ?? ''}`}
                />
                <ModalBody>
                    <AdjustComponent
                        termInfo={termInfo}
                        updateTermValue={updateTermValue}
                        updateTermStringValue={updateTermStringValue}
                        isAdjustableDataValid={isAdjustableDataValid}
                    />
                    {isOpcFetching ? (
                        <LoadingDots />
                    ) : (
                        <AdjustCoverageInfo adjustInformationProps={adjustInformationProps} />
                    )}
                    {!shouldHideDateText && <Message>Ændringen gælder fra i dag</Message>}
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={isOpcFetching || !isOpcDataFilled}
                        onClick={() => {
                            if (adjustAction === 'Deductible') {
                                const canGoNext = validateAdjustableDeductible();
                                // TODO: Use hippo label when label id is define in document [Hippo ACC]
                                if (canGoNext) {
                                    trackChangedCoverage();
                                    setRefreshCoverableChangesSelector({});
                                    handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                                }
                                return;
                            }
                            if (hasIncreaseInjuryDegree) {
                                setCallMeVariant('injuryDegree');
                                return;
                            }
                            if (
                                exceededDeathCoverageLimit ||
                                exceededPermanentInjuryInsuredSum ||
                                exceededChildPermanentInjuryInsuredSum
                            ) {
                                setCallMeVariant('deathCovExceedLimit');
                                return;
                            }

                            if (!isFromDAFlow && lowerAccInsuranceSumThanPrevious) {
                                setCallMeVariant('lowerAccInsuranceSumThanPrevious');
                                return;
                            }

                            trackChangedCoverage();
                            handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ACCEPT')}
                    </Button>
                    <Button
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(closeCallback);
                        }}
                        disabled={isOpcFetching}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-CANCEL')}
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default AdjustCoverageModal;
